import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { Button, Text } from '#atoms';
import { PATH } from '#const/path';
import useMyAccountQuery from '#hooks/queries/accounts/useMyAccountQuery';
import queryKeys from '#hooks/queries/queryKeys';
import { useDeleteScheduleCoachPersonalMutation } from '#hooks/queries/schedules/useDeleteScheduleCoachPersonalMutation';
import { useScheduleCoachPersonalDetailQuery } from '#hooks/queries/schedules/useScheduleCoachPersonalDetailQuery';
import { Modal } from '#molecules';
import { useToast } from '#providers/ToastProvider';
import { PrevHeaderBarThreeDotTemplate } from '#templates';
import { dateDayKoFormatter } from '#utils/dateFormatter';
import { LeftTextCheckBox } from 'src/components/atoms/LeftCheckBox';
import Loading from 'src/components/molecules/Loading';
import * as S from './styles';
interface DropdownMenuProps {
  onClickModify: () => void;
  onClickDelete: () => void;
}

export default function PersonalScheduleDetail() {
  const queryClient = useQueryClient();
  const { scheduleId } = useParams();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get('isAdmin') === 'Y';
  const { data, isLoading } = useScheduleCoachPersonalDetailQuery({
    personalScheduleId: scheduleId,
    isAdmin
  });
  const { data: userInfo } = useMyAccountQuery();
  const isMe = data?.coachId === userInfo?.id;
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  const todayDate = data && dateDayKoFormatter(data.startAt);
  const startTime = dayjs(data?.startAt).format('HH:mm');
  const endTime = dayjs(data?.endAt).format('HH:mm');
  const { mutate: deleteSchedulePersonal } =
    useDeleteScheduleCoachPersonalMutation();

  const handleDeleteSchedule = async () => {
    setIsOpenDeleteModal(false);
    deleteSchedulePersonal(
      {
        id: scheduleId as string
      },
      {
        onSuccess() {
          showToast({
            type: 'success',
            message: '일정이 삭제되었습니다.'
          });
          queryClient.invalidateQueries([queryKeys.SCHEDULE]);
          navigate(-1);
        },
        onError() {
          showToast({
            type: 'fail',
            message: '일정 삭제에 실패했습니다.'
          });
        }
      }
    );
  };

  const handleModify = () => {
    navigate(
      `${
        PATH.Weekly_Calender_Schedule_Registration
      }?personal-schedule-id=${encodeURIComponent(scheduleId as string)}`
    );
  };

  if (isLoading) {
    return <Loading />;
  }

  if (!scheduleId || !data) {
    return <div style={{ color: '#fff' }}>데이터가 없습니다</div>;
  }

  return (
    <PrevHeaderBarThreeDotTemplate
      defaultBackground
      disableDotButton={isAdmin}
      dotComponent={
        <DropdownMenu
          onClickModify={handleModify}
          onClickDelete={() => setIsOpenDeleteModal(true)}
        />
      }
    >
      <S.Container>
        <S.TimeHeader>
          <S.DateBox>{todayDate}</S.DateBox>
          <S.TimeBox>
            {startTime}
            <S.TimeDivider>-</S.TimeDivider>
            {endTime}
          </S.TimeBox>
        </S.TimeHeader>

        {!isMe && data?.coachName && (
          <S.TrainerInfo>
            <S.TrainerName>{data.coachName}</S.TrainerName>
            <S.TrainerText>트레이너</S.TrainerText>
          </S.TrainerInfo>
        )}

        <div>
          <S.LabeledInfo>
            <S.LabeledInfoTitle>제목</S.LabeledInfoTitle>
            <S.LabeledInfoDescription>{data?.title}</S.LabeledInfoDescription>
          </S.LabeledInfo>
        </div>
      </S.Container>

      {isOpenDeleteModal && (
        <Modal onClose={() => setIsOpenDeleteModal(false)}>
          <div>
            <Text style={{ textAlign: 'center' }}>
              선택하신 일정이 삭제돼요. <br /> 그래도 계속하시겠어요?
            </Text>
          </div>
          <LeftTextCheckBox id={'sameDayCancellation'} />
          <S.ModalButtonContainer>
            <Button
              background="anti"
              onClick={() => setIsOpenDeleteModal(false)}
            >
              취소
            </Button>
            <Button onClick={handleDeleteSchedule}>확인</Button>
          </S.ModalButtonContainer>
        </Modal>
      )}
    </PrevHeaderBarThreeDotTemplate>
  );
}

function DropdownMenu({ onClickModify, onClickDelete }: DropdownMenuProps) {
  return (
    <S.DropdownWrapper>
      <S.DropdownButton onClick={onClickModify}>수정</S.DropdownButton>
      <S.DropdownButtonDelete onClick={onClickDelete}>
        삭제
      </S.DropdownButtonDelete>
    </S.DropdownWrapper>
  );
}
